/**
 * TG-5 (m1-exposure-detail-sourcing): shared, pure helpers `OnFilePanel`
 * and `OnFilePicker` both need, and the one shape (`OnFileRenderContext`)
 * `ExposureQuestionsEngine.tsx` threads down from `ExposuresJourneyRoute`'s
 * own `useOnFileRead` hook, already scoped to ONE section instance by the
 * time it reaches here (that scoping is `ExposuresJourneyRoute`'s own job
 * — matching `OnFileSection.sectionInstanceId`, `on-file-port.ts`'s own
 * doc comment — not this module's).
 */
import { useCallback, useEffect, useRef } from "react";
import { pickedOnFileItemFor, type RepeatItemAnswer } from "./exposure-answers";
import type { OnFileRead, OnFileReadState } from "../../state/use-on-file-read";
import type { OnFileScreen, OnFileScreenItem } from "../../state/on-file-port";

/** One section instance's on-file read, already narrowed to that
 * instance's own screens — never a whole renewal's worth. */
export interface OnFileRenderContext {
  readonly state: OnFileReadState;
  readonly onRetry: () => void;
}

/**
 * Narrows `useOnFileRead`'s whole-renewal read down to exactly one
 * section instance (`OnFileSection.sectionInstanceId`, `on-file-port.ts`'s
 * own doc comment) -- the mandatory scoping step `ExposuresJourneyRoute`
 * does before this context ever reaches `SectionPageRoute`/
 * `ExposureQuestionsEngine`. Without it, a repeated section type (two
 * commercial-auto policies on one renewal, each with its own vehicles)
 * would let `findOnFileScreen`'s `screenKey` match resolve against
 * whichever policy's section happened to come first in the whole-renewal
 * list, not necessarily the one instance actually being rendered.
 */
export function scopeOnFileToInstance(read: OnFileRead, sectionInstanceId: string): OnFileRenderContext {
  if (read.state.status !== "succeeded") {
    return { state: read.state, onRetry: read.retry };
  }
  const scoped = read.state.sections.filter((section) => section.sectionInstanceId === sectionInstanceId);
  return { state: { status: "succeeded", sections: scoped }, onRetry: read.retry };
}

/**
 * Resolves every screen the on-file read carries whose `screenKey` is
 * `screenKey` or one of `siblingScreenKeys`, merging their items into
 * ONE `OnFileScreen` (OBJ-R-20/FIND-973): a section's mapped Epic line
 * types do not all store the one concept a help panel asks about under
 * the same field-map screen key (one of `exposure-commercial-property`'s
 * four mapped line types stores its premises on its own, separate
 * screen, while the other three land on `property_premise`), so
 * resolving only the primary key
 * silently discarded a sibling line type's own screen the read had
 * already grouped into the same section instance. `displayLabel` and
 * `fieldMapVersion` are taken from whichever matched screen is found
 * first -- both are cosmetic/diagnostic only here, never read by
 * `OnFilePanel`'s own rendering.
 */
export function findOnFileScreen(
  context: OnFileRenderContext | undefined,
  screenKey: string | undefined,
  siblingScreenKeys: readonly string[] = [],
): OnFileScreen | undefined {
  if (context?.state.status !== "succeeded" || screenKey === undefined) {
    return undefined;
  }
  const keys = new Set([screenKey, ...siblingScreenKeys]);
  const matched = context.state.sections
    .flatMap((section) => section.screens)
    .filter((screen) => keys.has(screen.screenKey));
  const primary = matched[0];
  if (primary === undefined) {
    return undefined;
  }
  const items = matched.flatMap((screen) => screen.items);
  return {
    screenKey: primary.screenKey,
    displayLabel: primary.displayLabel,
    fieldMapVersion: primary.fieldMapVersion,
    itemCount: items.length,
    items,
  };
}

/** Builds one item's one-line heading from the declared `labelAttributes`,
 * in order, skipping any the item does not carry (AC-3: a missing
 * attribute renders without that line, never a guessed value — the same
 * rule applied here to the heading as `OnFilePanel`'s own attribute dump
 * applies to the detail lines below it). */
export function onFileItemHeading(item: OnFileScreenItem, labelAttributes: readonly string[]): string {
  return labelAttributes
    .map((attribute) => onFileAttributeText(item.attributes[attribute]))
    .filter((text): text is string => text !== undefined)
    .join(" ");
}

/** The display text of one on-file attribute value, or `undefined` when it
 * has none (AC-10: no placeholder text). Only a non-blank string, a finite
 * number or a boolean renders; `null`, `undefined`, a blank string, an
 * object, an array and a non-finite number never print as `null`, an empty
 * line or `[object Object]`. Shared by the heading and the detail lines so
 * the two never disagree on what an item carries. */
export function onFileAttributeText(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value.trim() === "" ? undefined : value;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : undefined;
  }
  if (typeof value === "boolean") {
    return String(value);
  }
  return undefined;
}

const FLAG_WORDS = ["Sold", "Replaced", "Removed"] as const;

/**
 * SC 1.3.1: the Sold/Replaced/Removed flag for one on-file item, read by
 * matching `{screenKey, epicRecordId}` against every OTHER repeat item's
 * own pick for `pickerFieldId` (AC-2) — conveyed in TEXT (this word,
 * rendered in bold by the caller), never by colour alone. `undefined`
 * when no sibling item picked this one with a flagging outcome.
 */
export function onFileItemFlagWord(
  item: OnFileScreenItem,
  screenKey: string | undefined,
  pickerFieldId: string,
  allRepeatItems: readonly RepeatItemAnswer[],
): (typeof FLAG_WORDS)[number] | undefined {
  if (item.epicRecordId === null || screenKey === undefined) {
    return undefined;
  }
  for (const repeatItem of allRepeatItems) {
    const picked = pickedOnFileItemFor(repeatItem, pickerFieldId);
    if (
      picked?.screenKey === screenKey &&
      picked.epicRecordId === item.epicRecordId &&
      (FLAG_WORDS as readonly string[]).includes(repeatItem.outcome)
    ) {
      return repeatItem.outcome as (typeof FLAG_WORDS)[number];
    }
  }
  return undefined;
}

/** The on-file items already picked by every OTHER repeat item for
 * `pickerFieldId` on the SAME screen — the picker's own exclusion set
 * (AC-2: "withholds one already picked in another block"), identified by
 * `epicRecordId` since that is the stable identity `{screenKey,
 * epicRecordId}` carries across resume. */
export function excludedEpicRecordIds(
  screenKey: string | undefined,
  pickerFieldId: string,
  allRepeatItems: readonly RepeatItemAnswer[],
  excludeItemKey: string,
): ReadonlySet<number> {
  const excluded = new Set<number>();
  for (const repeatItem of allRepeatItems) {
    if (repeatItem.key === excludeItemKey) {
      continue;
    }
    const picked = pickedOnFileItemFor(repeatItem, pickerFieldId);
    if (picked !== undefined && picked.screenKey === screenKey) {
      excluded.add(picked.epicRecordId);
    }
  }
  return excluded;
}

/** H2 `soldLabels`: the on-file records another question has marked with `soldOutcome` (a
 * building sold in commercial property question 1), which drop out of a `soldAware` picker. */
export function soldEpicRecordIds(
  screenKey: string | undefined,
  pickerFieldId: string,
  sourceItems: readonly RepeatItemAnswer[],
  soldOutcome: string,
): ReadonlySet<number> {
  const sold = new Set<number>();
  for (const repeatItem of sourceItems) {
    const picked = pickedOnFileItemFor(repeatItem, pickerFieldId);
    if (repeatItem.outcome === soldOutcome && picked !== undefined && picked.screenKey === screenKey) {
      sold.add(picked.epicRecordId);
    }
  }
  return sold;
}

/**
 * One section instance can render SEVERAL independent data-on-file
 * questions (e.g. commercial auto's vehicles AND drivers), each with its
 * own retry control. All of them share the SAME underlying read, so all
 * of them become Failed at once -- without this, every one of their
 * `OnFilePanel`/`OnFilePicker` instances would call `.focus()` on its own
 * retry button in the same tick, and only the last one mounted would
 * actually end up focused (SC 2.4.3 names moving focus to THE retry
 * control, not racing several). `claimFailureFocus()` returns `true`
 * for the FIRST caller after a read becomes Failed and `false` for every
 * other caller until the read moves on (a retry attempt, or a fresh
 * section instance) -- `ExposureQuestionsEngine` holds the one ref this
 * is built from and passes the returned function down to every question
 * in the section, so exactly one retry control ever receives focus per
 * failure, deterministically the first one in question order.
 */
export function useFailureFocusClaim(status: OnFileReadState["status"] | undefined): () => boolean {
  const claimedRef = useRef(false);
  useEffect(() => {
    if (status !== "failed") {
      claimedRef.current = false;
    }
  }, [status]);
  // A stable function identity (useCallback, empty deps) -- it reads only
  // the ref above, never a value that would need to appear in its own
  // dependency array -- so a caller's effect can depend on it without
  // that effect re-running on every render this component makes.
  return useCallback(() => {
    if (claimedRef.current) {
      return false;
    }
    claimedRef.current = true;
    return true;
  }, []);
}

/** Whether a failed read forces a question's help panel open (and its
 * retry control takes focus): only when no picker for the question will
 * own that (`OnFilePanel`'s own `pickerDeclared`). */
export function isOnFilePanelForcedOpen(onFile: OnFileRenderContext | undefined, pickerDeclared: boolean): boolean {
  return onFile?.state.status === "failed" && !pickerDeclared;
}

/** The element id `PortalPanelBody` gives a help panel's content for the
 * panel `id` -- for a caller that points `aria-describedby` at it (REQ-006). */
export function onFilePanelBodyId(id: string): string {
  return `${id}-panel`;
}

/** Humanises a field-map/repeat attribute name the same way
 * `content.ts`'s `humanizeFieldId` already does for a field with no
 * Content-Design copy of its own — reused rather than redeclared so the
 * two never drift on the same kind of name. */
export { humanizeFieldId } from "./content";
